import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { homePath } from "@/lib/academyAccess";
import { LOGIN } from "@/constants/testIds";

// Fix H: un mensaje distinto por causa, para saber que paso si el error vuelve a aparecer.
function loginErrorMessage(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  if (!status) return "No pudimos conectar con el servidor. Revisá tu conexión e intentá de nuevo.";
  return `El servidor no respondió bien (error ${status}). Intentá de nuevo en unos segundos.`;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password, () => setRetrying(true));
      navigate(homePath(user));
    } catch (err) {
      console.warn("[login] fallo", { status: err.response?.status, code: err.code });
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h1 className="font-serif text-3xl tracking-[0.1em] text-brand mb-1">AAME</h1>
          <p className="text-sm text-muted-foreground mb-8">Academy &middot; Gestión de la academia</p>

          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-foreground mb-6">Ingresar</h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" data-testid={LOGIN.emailInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Contraseña</Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="********" className="pr-10" data-testid={LOGIN.passwordInput} />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-r-md"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error-message">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-white" disabled={loading} data-testid={LOGIN.submitButton}>
              {loading ? (retrying ? "Reconectando..." : "Ingresando...") : "Ingresar"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            ¿No tenés cuenta o te olvidaste la contraseña? Pedísela a la administración de la academia.
          </p>
        </div>
      </div>

      {/* Fix G (2026-10-19): panel turquesa de AAME, con su lema. */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-gradient-to-br from-[#025E67] via-[#037782] to-[#199095]">
        <div className="max-w-sm text-center px-10 text-white">
          <p className="font-serif text-5xl tracking-[0.2em]">AAME</p>
          <p className="text-sm tracking-[0.3em] uppercase text-white mt-2">Academy</p>
          <p className="font-serif text-lg mt-8">American Aesthetics Medical Education</p>
          <p className="text-sm text-white mt-3">Formación que impulsa tu futuro profesional</p>
        </div>
      </div>
    </div>
  );
}
